import {
    HttpEvent,
    HttpEventType,
} from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IMLPackage,
    IMLPackageVersion,
    IMLSkill,
    MLPackageStatus,
    MLProcessor,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MAX_NUMBER_OF_RESULTS } from '@pages/shared/constants';
import {
    MLPackagesService,
    MLPackageVersionsService,
    MLSkillsService,
    ODataQueryOptions,
} from '@services';
import {
    ISuggestValue,
    ISuggestValueData,
} from '@uipath/angular/components/ui-suggest';
import UiValidators, {
    UI_DESCRIPTION_MAX_LENGTH,
    UI_NAME_MAX_LENGTH,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
    of,
    throwError,
} from 'rxjs';
import {
    catchError,
    finalize,
    map,
    share,
    tap,
} from 'rxjs/operators';

@Component({
    templateUrl: './mlskills-add.modal.html',
    styleUrls: ['./mlskills-add.modal.scss'],
    providers: [
        FormBuilder,
        MLPackagesService,
        MLPackageVersionsService,
        MLSkillsService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class MLSkillsModalComponent
    extends UiModalComponent<IMLSkill>
    implements OnInit {

    public get selectedMLSkill() {
        return this.context.data.modelId ?
            {
                id: this.context.data.modelId,
                text: this.context.data.modelId,
            } :
            null;
    }

    public get selectedType() {
        return this.processorTypes.filter(processorType => processorType.id === this.context.data.processor);
    }

    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);
    public models$?: Observable<ISuggestValueData<IMLPackageVersion>[]>;
    public resultSize = MAX_NUMBER_OF_RESULTS;

    public processorTypes: ISuggestValue[] = [];

    private _mlpackagesService = this._injector.get<MLPackagesService>(MLPackagesService);
    private _mlpackageversionsService = this._injector.get<MLPackageVersionsService>(MLPackageVersionsService);
    private _mlskillsService = this._injector.get<MLSkillsService>(MLSkillsService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);

    private _selectedMLPackageVersionId?: string;

    ngOnInit() {

        // Currently only CPU is supported
        this.processorTypes = [MLProcessor.CPU]
            .map(processor => ({
                id: processor,
                text: processor,
            }));

        this.context.data.processor = MLProcessor[this.processorTypes[0].text as keyof typeof MLProcessor];

        this.form = this._injector.get<FormBuilder>(FormBuilder).group({
            'modelId': [
                [this.selectedMLSkill],
                UiValidators.required(),
            ],
            'modelVersionId': [
                [], (c: FormControl) => {
                    return this.selectedMLSkill == null ? null : UiValidators.required()(c);
                },
            ],
            'name': [
                '',
                [Validators.maxLength(UI_NAME_MAX_LENGTH), UiValidators.required()],
            ],
            'processor': [
                [this.context.data.processor],
                UiValidators.required(),
            ],
            'description': [
                '',
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
        });
    }

    public mlskillSearchFactory = (query: string = '', top: number = MAX_NUMBER_OF_RESULTS, skip: number = 0) => {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['Name asc'],
            filter: {
                Name: {
                    contains: query,
                },
            },
            skip,
            top,
        };

        return this._mlpackagesService
            .get(queryOptions)
            .pipe(
                map(response => this._filterAndMapDeployableMLPackages(response.data)),
                map(models => ({
                    data: models,
                    total: models.length,
                })),
            );
    }

    public fetchModelVersions() {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['Version desc'],
        };

        this.models$ = this._mlpackageversionsService
            .getVersions(this.context.data.modelId, queryOptions)
            .pipe(
                map(result =>
                    !result || !result.total ?
                        [] :
                        this._filterAndMapDeployableMLPackageVersions(result.data),
                ),
                tap(models => {
                    if (models && models.length > 0) {
                        this.form.patchValue({ 'modelVersionId': [models[0]] });
                        this.onVersionSelect(models[0]);
                    } else {
                        this.form.patchValue({ 'modelVersionId': [] });
                    }
                }),
                share(),
            );
    }

    public onSubmit(_ev?: Event) {
        if (_ev) {
            _ev.preventDefault();
            _ev.stopImmediatePropagation();
        }

        this.loading$.next(true);

        const payload = cloneDeep(this.context.data);
        payload.description = this.form.get('description')!.value;
        payload.name = this.form.get('name')!.value.trim();

        this._mlskillsService.createMLSkill(payload, this._selectedMLPackageVersionId!)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
                catchError(error => {
                    if (error.error && error.error.respMsg) {
                        this._snack.error(this._translateService.instant(`mlskill_creation_generic_failure`, {
                            errorMessage: error.error.respMsg,
                        }));
                        return of(error);
                    }
                    return throwError(error);
                }),
            )
            .subscribe((ev: HttpEvent<any>) => {
                if (ev.type === HttpEventType.Response) {
                    this._dialogRef.close({
                        type: ModalResultType.Add,
                    });
                }
            });
    }

    public onVersionSelect(value: ISuggestValueData<IMLPackageVersion>) {
        this.context.data.currentVersion = value.data!.version;
        this._selectedMLPackageVersionId = value.data!.id;
    }

    private _filterAndMapDeployableMLPackageVersions = (models: IMLPackageVersion[]) => {
        return models
            .filter((model) => this._canDeployPackage(model.status))
            .map((model: IMLPackageVersion) => {
                return {
                    id: model.id,
                    text: String(model.version),
                    data: model,
                };
            }) as ISuggestValueData<IMLPackageVersion>[];
    }

    private _filterAndMapDeployableMLPackages = (models: IMLPackage[]) => {
        return models
            .filter((model) => this._canDeployPackage(model.status))
            .map((model: IMLPackage) => {
                return {
                    id: model.id,
                    text: model.name,
                };
            });
    }

    private _canDeployPackage(packageStatus: string) {
        return packageStatus.toLowerCase() === MLPackageStatus.Undeployed.toLowerCase() ||
            packageStatus.toLowerCase() === MLPackageStatus.Deployed.toLowerCase() ||
            packageStatus.toLowerCase() === MLPackageStatus.Deploying.toLowerCase() ||
            packageStatus.toLowerCase() === MLPackageStatus.Available.toLowerCase();
    }
}
